import React, { useCallback } from 'react';

import { ComingSoonTile } from './ActualsDashboardTile';
import {
    AgeingTile,
    ByDepartmentTile,
    CashflowComparisonTile,
    CommitPipelineTile,
    ImportHealthTile,
    ReconciliationProgressTile,
    TopSuppliersTile,
} from './ActualsDashboardTiles';
import ActualsSummaryTile from './ActualsSummaryTile';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import useQuery from 'Hooks/useQuery';

/**
 * The Actuals module's landing tab.
 *
 * Every figure here excludes archived transactions — a client rule the server
 * enforces, so nothing on this side filters them out again.
 */
export default function ActualsDashboard({ dashboardUrl, uploadUrl, onNavigate, totals, totalsLoading }) {
    const dashboardRequest = useQuery({
        queryFn: () => axios.get(dashboardUrl),
    });

    const data = dashboardRequest.data;

    const navigate = useCallback(
        (tab, type) => {
            onNavigate?.(tab, type);
        },
        [onNavigate],
    );

    if (dashboardRequest.loading || !data) {
        return <LoadingSpinner />;
    }

    /* An event with no actuals at all, and an event whose only actuals are
       archived, both land here. The second is why this is an empty state and
       not a grid of zeros: the global scope hides archived rows from every
       read, so every tile would legitimately show zero for an event that
       plainly has history — which would read as a healthy process when it is
       the opposite. */
    if (data.empty) {
        return (
            <div className="tw-py-12 tw-px-4 tw-text-center">
                <p className="tw-text-base tw-font-medium tw-text-charcoal">{$t('Nothing to report on yet')}</p>
                <p className="tw-mx-auto tw-mt-2 tw-max-w-md tw-text-sm tw-text-charcoal-50">
                    {$t(
                        'Import actual costs to see reconciliation progress, ageing and supplier patterns here. Archived transactions are excluded from every figure.',
                    )}
                </p>
                {!!uploadUrl && (
                    <div className="tw-mt-6 tw-flex tw-justify-center">
                        <Button small as="a" variant="primary" href={uploadUrl}>
                            {$t('Import actual costs')}
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    const pendingAssessment = Number(data.pending_assessment_count) || 0;

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-4">
            {/* Transactions imported but not yet assessed appear in neither
                reconciliation tab, so the grouped tiles below exclude them. Said
                out loud rather than absorbed: without this the tiles read lower
                than the progress tile for no visible reason, and the gap looks
                like a contradiction instead of work still in flight. */}
            {pendingAssessment > 0 && (
                <Alert type="info" role="status">
                    {$t(
                        '{{count}} transaction(s) are still being assessed and are not counted in the figures below. They will appear once processing finishes.',
                        { count: pendingAssessment },
                    )}
                </Alert>
            )}

            <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-3">
                <ActualsSummaryTile totals={totals} loading={totalsLoading} onNavigate={navigate} />

                <ReconciliationProgressTile data={data} onNavigate={navigate} />
                <ImportHealthTile data={data} onNavigate={navigate} />
                <AgeingTile data={data} onNavigate={navigate} />

                <ByDepartmentTile data={data} onNavigate={navigate} />
                <TopSuppliersTile data={data} onNavigate={navigate} />
                <CashflowComparisonTile data={data} />

                <ComingSoonTile
                    title={$t('Committed on actuals')}
                    description={$t('Actuals committed straight to budget lines, split by department, once committing from an actual is available.')}
                />
                <CommitPipelineTile data={data} onNavigate={navigate} />
                <ComingSoonTile
                    title={$t('Threshold watch')}
                    description={$t('Suppliers whose repeated actuals suggest a proper commitment is needed, once a threshold can be set.')}
                />
            </div>
        </div>
    );
}
